import { AppLink } from '@/components/ui/app-link';
import type { DocumentRenderTemplate } from '@vivport/backend/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { isWriteUpTaskRunningFor, useRunWriteUpTask } from '@/hooks/useTemplateQueries';
import { useAsyncStore } from '@/store/asyncStore';
import { toContentHref } from '@/utils/content-ref';
import { LayoutTemplate } from 'lucide-react';

interface TemplateInstanceStripProps {
  template: DocumentRenderTemplate;
  instanceRootId: number;
  instanceTitle: string | null;
}

// The write-up runs through the same governed templates.writeUp tool Neo calls, as an AsyncQueue
// task under the member's ManualSession apex. The chip is the audit-derived written-ness fact;
// the button's disabled state comes from the task queue, never from content state.
export function TemplateInstanceStrip({
  template,
  instanceRootId,
  instanceTitle,
}: TemplateInstanceStripProps) {
  const writeUp = template.writeUp;
  const tasks = useAsyncStore((state) => state.tasks);
  const running = isWriteUpTaskRunningFor(tasks, instanceRootId);
  const runWriteUp = useRunWriteUpTask();

  return (
    <div
      data-testid="template-instance-strip"
      className="mb-4 flex flex-wrap items-center gap-3 rounded-md border bg-muted/40 px-3 py-2 text-sm"
    >
      <LayoutTemplate className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0">
        Instance of{' '}
        <AppLink className="font-medium underline underline-offset-2" to={toContentHref(template.templateCode)}>
          {template.name}
        </AppLink>
      </span>
      {writeUp !== undefined ? (
        <Badge variant={writeUp.written ? 'default' : 'secondary'}>
          {writeUp.written ? 'Written up' : 'Not yet written up'}
        </Badge>
      ) : null}
      {writeUp !== undefined && writeUp.unresolvedRequiredFields > 0 ? (
        <Badge variant="outline">{writeUp.unresolvedRequiredFields} detail(s) to fill</Badge>
      ) : null}
      {writeUp !== undefined ? (
        <Button
          size="sm"
          variant="outline"
          disabled={running}
          onClick={() => {
            void runWriteUp({
              instanceContentId: instanceRootId,
              instanceTitle: instanceTitle ?? template.name,
            });
          }}
        >
          {writeUp.written ? 'Write up again' : 'Write up now'}
        </Button>
      ) : null}
    </div>
  );
}
